import { useEffect, useState } from "react";
import { ADMIN_API_URL } from "../../utils/env";
import { createFundingEvidence, fundingEvidenceEnabled } from "./fundingEvidenceProjection";

export function FundingEvidence({ data, requestedAccount }: { data: unknown; requestedAccount: string }) {
  const endpoint = ADMIN_API_URL("account");
  const enabled = fundingEvidenceEnabled(process.env.REACT_APP_ENVIRONMENT, endpoint);
  const [snapshot, setSnapshot] = useState<{ account: string; text: string }>();
  const [status, setStatus] = useState("");
  useEffect(() => { setSnapshot(undefined); setStatus(""); }, [requestedAccount, endpoint, enabled]);
  if (!enabled) return null;
  const text = snapshot?.account === requestedAccount ? snapshot.text : undefined;

  const capture = () => {
    setSnapshot(undefined);
    try {
      const next = createFundingEvidence(data, requestedAccount, {
        endpoint, frontendOrigin: window.location.origin, capturedAt: new Date().toISOString(),
      });
      setSnapshot({ account: requestedAccount, text: next });
      setStatus("Captured the displayed account data. Review the exact fields below.");
    } catch {
      setStatus("Evidence unavailable: account identity or required fields are missing or invalid.");
    }
  };
  const copy = async () => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); setStatus("Funding evidence copied."); } catch { setStatus("Copy failed. Use Download evidence or select the displayed text."); }
  };
  const download = () => {
    if (!text) return;
    const url = URL.createObjectURL(new Blob([`${text}\n`], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `funding-evidence-${requestedAccount.toLowerCase()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <details style={{ margin: "16px 0" }}>
      <summary>Inspect funding evidence</summary>
      <p>Reload this account before capturing. Exported values come from the loaded account response without rounding.
        The capture time is your browser&apos;s time, not the server&apos;s response time.</p>
      <button type="button" onClick={capture}>Capture displayed fields</button>{" "}
      <button type="button" disabled={!text} onClick={copy}>Copy evidence</button>{" "}
      <button type="button" disabled={!text} onClick={download}>Download evidence</button>
      <p role="status">{status}</p>
      {text && <pre aria-label="Funding evidence JSON" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{text}</pre>}
    </details>
  );
}
